import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Harp, { harpStrings, HARP_KEYS, letterIndexOf } from "@/components/Harp";
import LetterStrip from "@/components/LetterStrip";
import HebrewKeyboard from "@/components/HebrewKeyboard";
import PlayButton from "@/components/PlayButton";
import StudioAuthNav from "@/components/StudioAuthNav";
import LabStamp from "@/components/LabStamp";
import {
  DEFAULT_MODE,
  DEFAULT_ROOT,
  DEFAULT_SCALE,
  HEBREW_BASE_LETTERS,
  MODES,
  ROOTS,
  SCALES,
  alefNoteForMode,
  tokenizeHebrew,
} from "@/lib/hebrewCryptogram";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import { pluckWordStrings } from "@/lib/pluckPhrasing";
import { pickRandomHebrewPhrase } from "@/lib/hebrewWords";
import { playHarp, setReverbMix, unlockAudio } from "@/lib/synth";
import { playSequenceAudio, playEventsExact } from "@/lib/playEngine";
import {
  BPM_MAX,
  BPM_MIN,
  PLAYBACK_MODES,
  defaultBpmFor,
  defaultSustainFor,
  reverbMixForSustain,
} from "@/lib/presets";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_LETTERS = 400;
const DEFAULT_PRESET = "meditation";
// The harp has its own feel: plucked strings drag at the pad's tempo, so
// Meditative starts brisker here and the strings ring a little shorter.
const HARP_BPM = { meditation: 120, groove: 108 };
const HARP_SUSTAIN = { meditation: 85, groove: 100 };
const harpBpmFor = (mode) => HARP_BPM[mode] ?? defaultBpmFor(mode);
const harpSustainFor = (mode) => HARP_SUSTAIN[mode] ?? defaultSustainFor(mode);

export default function HarpStudio() {
  const [searchParams] = useSearchParams();
  const [inputText, setInputText] = useState(() => searchParams.get("words") || "");
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [root, setRootValue] = useState(() => searchParams.get("root") || DEFAULT_ROOT);
  const [scale, setScaleValue] = useState(() => {
    const s = searchParams.get("scale");
    return SCALES.some((x) => x.value === s) ? s : DEFAULT_SCALE;
  });
  const [modeIdx, setModeIdxValue] = useState(() => {
    const m = parseInt(searchParams.get("mode") || "", 10);
    return Number.isFinite(m) && m >= 0 && m <= 6 ? m : DEFAULT_MODE;
  });
  const [playbackMode, setPlaybackMode] = useState(DEFAULT_PRESET);
  const [bpmByMode, setBpmByMode] = useState(() => ({
    meditation: harpBpmFor("meditation"),
    groove: harpBpmFor("groove"),
  }));
  const [sustainByMode, setSustainByMode] = useState(() => ({
    meditation: harpSustainFor("meditation"),
    groove: harpSustainFor("groove"),
  }));
  const [daily, setDaily] = useState({ loading: true, error: false, sources: [] });
  const [loadedSource, setLoadedSource] = useState(null);
  // Which strings have rung, and where playback is in the phrase.
  const [lit, setLit] = useState(() => new Set());
  const [playedKeys, setPlayedKeys] = useState(() => new Set());
  const [activeKey, setActiveKey] = useState(null);
  // What's been plucked by hand, for the strip when nothing is typed.
  const [pluckLog, setPluckLog] = useState([]);
  // Replay a plucked run with the player's own rhythm rather than the grid.
  const [exactTiming, setExactTiming] = useState(true);

  const harpRef = useRef(null);
  const inputRef = useRef(null);
  const stopRef = useRef(false);
  const playRunRef = useRef(0);

  const bpm = bpmByMode[playbackMode] ?? harpBpmFor(playbackMode);
  const sustain = sustainByMode[playbackMode] ?? harpSustainFor(playbackMode);
  const bpmRef = useRef(bpm);
  const sustainRef = useRef(sustain);
  bpmRef.current = bpm;
  sustainRef.current = sustain;
  const atFeelDefaults =
    bpm === harpBpmFor(playbackMode) && sustain === harpSustainFor(playbackMode);

  useEffect(() => {
    setReverbMix(reverbMixForSustain(sustain));
  }, [sustain]);

  const words = useMemo(
    () => inputText.split(/\s+/).filter((w) => w.length > 0),
    [inputText]
  );
  const letterCount = useMemo(
    () => words.reduce((acc, w) => acc + tokenizeHebrew(w).length, 0),
    [words]
  );
  const tooManyLetters = letterCount > MAX_LETTERS;

  const scaleOptions = useMemo(
    () => ({ root, modeIdx, scale, condensed: false }),
    [root, modeIdx, scale]
  );
  const sequence = useMemo(
    () => buildHebrewSequence(words, scaleOptions),
    [words, scaleOptions]
  );
  const strings = useMemo(() => harpStrings(scaleOptions), [scaleOptions]);
  const modeOptions = useMemo(
    () =>
      MODES.map((m) => ({
        value: m.value,
        label: `Aleph on ${alefNoteForMode(root, m.value, scale)}`,
      })),
    [root, scale]
  );

  const loadDaily = useCallback(async () => {
    setDaily((d) => ({ ...d, loading: true, error: false }));
    try {
      const { data } = await axios.get(`${API}/torah/daily`, {
        params: { weekday: new Date().getDay() },
      });
      setDaily({ loading: false, error: false, sources: data.sources || [] });
    } catch (err) {
      setDaily({ loading: false, error: true, sources: [] });
    }
  }, []);

  useEffect(() => {
    loadDaily();
  }, [loadDaily]);

  const clearHarpState = () => {
    setLit(new Set());
    setPlayedKeys(new Set());
    setActiveKey(null);
    setPluckLog([]);
  };

  // A single string, plucked by hand (or by a key press). With nothing typed,
  // the strip above the harp keeps a running read-out of what's been plucked.
  const strikeString = useCallback((s) => {
    if (!s || !s.pitch) return;
    unlockAudio();
    playHarp(s.pitch, 2.6 * Math.max(0.2, sustainRef.current / 100), 0.7);
    setLit((prev) => new Set(prev).add(s.letterIdx));
    setPluckLog((prev) =>
      [...prev, { letter: s.letter, letterIdx: s.letterIdx, pitch: s.pitch, t: Date.now() }].slice(-200)
    );
  }, []);

  // Drop the last string you hit — the run is only as good as its last note.
  const undoPluck = useCallback(() => {
    setPluckLog((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.slice(0, -1);
      setLit(new Set(next.map((p) => p.letterIdx)));
      setSavedId(null);
      return next;
    });
  }, []);

  // Physical keyboard across the strings, left to right.
  useEffect(() => {
    const onKey = (e) => {
      if (isPlaying || showKeyboard || e.repeat) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Backspace") {
        e.preventDefault();
        undoPluck();
        return;
      }
      const d = HARP_KEYS.indexOf(e.key.toLowerCase());
      if (d < 0 || d >= strings.length) return;
      e.preventDefault();
      const s = strings[d];
      harpRef.current?.pluck(s.letterIdx);
      strikeString(s);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [strings, strikeString, isPlaying, showKeyboard, undoPluck]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    setLoadedSource(null);
    setSavedId(null);
    if (!isPlaying) clearHarpState();
  };

  const applyDailySource = (src) => {
    setInputText(src.hebrew);
    setLoadedSource(src);
    setSavedId(null);
    clearHarpState();
  };

  const insertAtCaret = (text) => {
    const el = inputRef.current;
    if (!el || typeof el.selectionStart !== "number") {
      setInputText((prev) => prev + text);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setInputText(el.value.slice(0, start) + text + el.value.slice(end));
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;
      const pos = start + text.length;
      node.focus();
      node.setSelectionRange(pos, pos);
    });
  };

  const handleKeyboardBackspace = () => {
    const el = inputRef.current;
    if (!el || typeof el.selectionStart !== "number") {
      setInputText((prev) => [...prev].slice(0, -1).join(""));
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start !== end) setInputText(el.value.slice(0, start) + el.value.slice(end));
    else if (start > 0) setInputText(el.value.slice(0, start - 1) + el.value.slice(start));
  };

  const reset = () => {
    stopRef.current = true;
    playRunRef.current += 1;
    setIsPlaying(false);
    clearHarpState();
  };

  // Plays a sequence of entries on the harp, lighting the strip and the strings
  // as it goes. Used by Play (whole phrase) and by tapping a word in the strip.
  const runSequence = async (seq, { keepLit = false } = {}) => {
    if (isPlaying || !seq || seq.length === 0) return;
    unlockAudio();
    stopRef.current = false;
    const runId = playRunRef.current + 1;
    playRunRef.current = runId;
    const isStale = () => playRunRef.current !== runId;
    setIsPlaying(true);
    if (!keepLit) setLit(new Set());
    setPlayedKeys(new Set());
    setActiveKey(null);
    const played = new Set();
    const playedLetters = new Set(keepLit ? lit : []);
    await playSequenceAudio(seq, {
      presetKey: playbackMode,
      voice: "harp",
      bpm: () => bpmRef.current,
      sustain: () => sustainRef.current,
      pedal: false,
      shouldStop: () => stopRef.current || isStale(),
      onTick: (i, entry) => {
        if (isStale()) return;
        const key = `${entry.wordIdx}:${entry.charIdx}`;
        played.add(key);
        setPlayedKeys(new Set(played));
        setActiveKey(key);
        const letterIdx = HEBREW_BASE_LETTERS.indexOf(entry.baseLetter);
        if (letterIdx < 0) return;
        playedLetters.add(letterIdx);
        setLit(new Set(playedLetters));
        harpRef.current?.pluck(letterIdx);
      },
    });
    if (isStale()) return;
    setIsPlaying(false);
    setActiveKey(null);
  };

  const handlePlay = () => {
    if (tooManyLetters) return;
    if (!usingTyped && exactTiming && exactEvents.length > 0) {
      runExact(exactEvents);
      return;
    }
    runSequence(activeSequence);
  };

  // Verbatim replay of a hand-plucked run — your own gaps, not the tempo grid.
  const runExact = async (events) => {
    if (isPlaying || events.length === 0) return;
    unlockAudio();
    stopRef.current = false;
    const runId = playRunRef.current + 1;
    playRunRef.current = runId;
    const isStale = () => playRunRef.current !== runId;
    setIsPlaying(true);
    setLit(new Set());
    setPlayedKeys(new Set());
    setActiveKey(null);
    const played = new Set();
    const rung = new Set();
    await playEventsExact(events, {
      voice: "harp",
      shouldStop: () => stopRef.current || isStale(),
      onNote: (ev) => {
        if (isStale()) return;
        played.add(keyOf(ev));
        setPlayedKeys(new Set(played));
        setActiveKey(keyOf(ev));
        const letterIdx = letterIndexOf(ev);
        if (letterIdx < 0) return;
        rung.add(letterIdx);
        setLit(new Set(rung));
        harpRef.current?.pluck(letterIdx);
      },
    });
    if (isStale()) return;
    setIsPlaying(false);
    setActiveKey(null);
  };

  const playWord = (wordIdx) => {
    if (isPlaying) return;
    runSequence(
      activeSequence.filter((e) => e.wordIdx === wordIdx),
      { keepLit: true }
    );
  };

  const handleSave = async () => {
    const cleaned = activeWords.map((w) => w.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      toast.error("Pluck a few strings or type a word before saving.");
      return null;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/compositions`,
        {
          title: cleaned.join(" ").slice(0, 60),
          words: cleaned,
          preset_key: playbackMode,
          pedal: false,
          scale_root: root,
          scale_mode: modeIdx,
          scale_family: scale,
          instrument: "harp",
          ...(!usingTyped && exactTiming && exactEvents.length > 0
            ? { remix_events: exactEvents }
            : {}),
          ...(loadedSource
            ? {
                source_label: `${loadedSource.label}${loadedSource.display ? ` · ${loadedSource.display}` : ""}`,
                source_ref: loadedSource.ref,
              }
            : {}),
        },
        { withCredentials: true }
      );
      setSavedId(data?.id || null);
      toast.success(usingTyped ? "Saved to your library" : "Plucked run saved to your library");
      return data?.id || null;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ? `Couldn't save: ${formatApiErrorDetail(e)}` : "Couldn't save"
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = async () => {
    const id = savedId || (await handleSave());
    if (!id) return;
    const url = `${window.location.origin}/composition/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      toast.message("Share link", { description: url, duration: 8000 });
    }
  };

  const labelStyle = {
    color: "#5C5268",
    fontFamily: "'Courier Prime', monospace",
  };

  // The strip reads the typed phrase; with nothing typed it reads the plucks,
  // with the player's own pauses deciding where the words break.
  const pluckWords = useMemo(() => pluckWordStrings(pluckLog), [pluckLog]);
  const usingTyped = words.length > 0;
  const activeWords = usingTyped ? words : pluckWords;
  const pluckSequence = useMemo(
    () => (usingTyped ? [] : buildHebrewSequence(pluckWords, scaleOptions)),
    [usingTyped, pluckWords, scaleOptions]
  );
  const activeSequence = usingTyped ? sequence : pluckSequence;
  // The run as verbatim events: each pluck keeps the gap you actually left
  // after it, so "my timing" replays your own rhythm instead of the grid.
  const exactEvents = useMemo(() => {
    if (usingTyped || pluckSequence.length === 0) return [];
    const noteSec = 2.6 * Math.max(0.2, sustain / 100);
    return pluckSequence.map((e, i) => ({
      kind: "note",
      ...e,
      displayWordIdx: e.wordIdx,
      stepMs:
        i < pluckLog.length - 1
          ? Math.max(40, pluckLog[i + 1].t - pluckLog[i].t)
          : 700,
      noteSec,
      velocity: 0.7,
    }));
  }, [usingTyped, pluckSequence, pluckLog, sustain]);

  const keyOf = (e) => `${e.wordIdx}:${e.charIdx}`;
  const allPluckKeys = useMemo(
    () => new Set(pluckSequence.map(keyOf)),
    [pluckSequence]
  );
  const stripSequence = activeSequence;
  const stripPlayed = usingTyped || isPlaying ? playedKeys : allPluckKeys;
  const stripActive =
    usingTyped || isPlaying
      ? activeKey
      : pluckSequence.length
      ? keyOf(pluckSequence[pluckSequence.length - 1])
      : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAF8F5", color: "#1A1523" }}>
      <Toaster position="bottom-right" richColors />

      <header className="max-w-[1180px] mx-auto px-5 sm:px-6 pt-8 sm:pt-10 pb-6 flex flex-wrap items-end justify-between gap-4 sm:gap-6">
        <div className="min-w-0 flex-1">
          <LabStamp testId="harp-lab-stamp" />
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            data-testid="harp-title"
          >
            David&apos;s Harp<span style={{ color: "#E86F4A" }}>.</span>
          </h1>
          <p className="mt-3 text-sm max-w-[560px] leading-relaxed" style={{ color: "#5C5268" }}>
            The same 22 letters, strung instead of written — aleph the longest string on
            the right, tav the shortest on the left. Pluck a string, or type a word and
            hear it played across the harp.
          </p>
        </div>
        <nav className="text-sm shrink-0 flex gap-4 items-center">
          <StudioAuthNav />
          <Link
            to="/app/hebrew"
            className="underline underline-offset-4 hover:text-[#E86F4A]"
            data-testid="harp-nav-studio"
          >
            Notation studio &rsaquo;
          </Link>
          <Link
            to="/app/library"
            className="underline underline-offset-4 hover:text-[#E86F4A]"
            data-testid="harp-nav-library"
          >
            Library &rsaquo;
          </Link>
        </nav>
      </header>

      <main className="max-w-[1180px] mx-auto px-5 sm:px-6 pb-20 space-y-6 sm:space-y-8">
        <section
          className="rounded-md p-4 sm:p-6 border"
          style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <h2 className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Type or paste Hebrew
            </h2>
            <div className="flex gap-4 text-xs" style={{ color: "#5C5268" }}>
              <span>
                {words.length} {words.length === 1 ? "word" : "words"}
              </span>
              <span style={{ color: tooManyLetters ? "#D99B26" : "#5C5268" }}>
                {letterCount} / {MAX_LETTERS} letters
              </span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="harp-input"
              ref={inputRef}
              value={inputText}
              onChange={handleInputChange}
              dir="rtl"
              placeholder="שלום אהבה אור"
              className="flex-1 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] placeholder:text-[#5C5268] h-12 text-xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            />
            <Button
              data-testid="harp-inspire-button"
              onClick={() => {
                setInputText(pickRandomHebrewPhrase(5, 24));
                clearHarpState();
              }}
              disabled={isPlaying}
              variant="outline"
              className="h-12 rounded-md border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 hover:text-[#1A1523] active:text-[#1A1523] whitespace-nowrap w-full sm:w-auto"
              style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
            >
              ✦ Inspire me
            </Button>
            <Button
              data-testid="harp-keyboard-toggle"
              onClick={() => setShowKeyboard((v) => !v)}
              variant="outline"
              className="h-12 rounded-md border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 hover:text-[#1A1523] active:text-[#1A1523] whitespace-nowrap w-full sm:w-auto"
              style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
            >
              {showKeyboard ? "Hide keyboard" : "Keyboard"}
            </Button>
          </div>
          <p className="mt-2 text-xs" style={{ color: tooManyLetters ? "#D99B26" : "#5C5268" }}>
            {tooManyLetters
              ? `That's a lot of letters — keep the total to ${MAX_LETTERS} or fewer.`
              : "Separate words with spaces. Drag across the strings for a glissando, or use the keys marked under each string."}
          </p>

          {/* Today's learning, straight from Sefaria */}
          <div className="mt-5 pt-4" style={{ borderTop: "1px solid #EBE3D5" }}>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="text-[10px] uppercase mr-1"
                style={{ ...labelStyle, letterSpacing: "0.18em" }}
              >
                Today&apos;s learning
              </span>
              {daily.loading && (
                <span className="text-xs" style={{ color: "#5C5268" }} data-testid="harp-daily-loading">
                  asking Sefaria…
                </span>
              )}
              {daily.error && (
                <button
                  type="button"
                  onClick={loadDaily}
                  className="text-xs underline underline-offset-4"
                  style={{ color: "#8A6514" }}
                  data-testid="harp-daily-retry"
                >
                  Couldn&apos;t reach Sefaria — try again
                </button>
              )}
              {daily.sources.map((src) => (
                <button
                  key={src.key}
                  type="button"
                  onClick={() => applyDailySource(src)}
                  disabled={isPlaying}
                  data-testid={`harp-daily-source-${src.key}`}
                  title={src.ref}
                  className="h-9 px-3 rounded-md text-xs transition-colors disabled:opacity-40"
                  style={{
                    fontFamily: "'Courier Prime', monospace",
                    letterSpacing: "0.1em",
                    border: "1px solid #EBE3D5",
                    backgroundColor:
                      loadedSource?.key === src.key ? "rgba(217,155,38,0.16)" : "#FFFFFF",
                    color: loadedSource?.key === src.key ? "#8A6514" : "#1A1523",
                  }}
                >
                  {src.label}
                </button>
              ))}
            </div>
            {loadedSource && (
              <div className="mt-3" data-testid="harp-daily-loaded">
                <p className="text-xs" style={{ color: "#E86F4A", fontFamily: "'Courier Prime', monospace" }}>
                  {loadedSource.label}
                  {loadedSource.display ? ` · ${loadedSource.display}` : ""} · {loadedSource.ref}
                </p>
                {loadedSource.english && (
                  <p className="mt-1.5 text-sm max-w-[80ch]" style={{ color: "#5C5268", lineHeight: 1.65 }}>
                    {loadedSource.english}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {showKeyboard && (
          <HebrewKeyboard theme="light"
            onLetter={(ch) => insertAtCaret(ch)}
            onSpace={() => insertAtCaret(" ")}
            onBackspace={handleKeyboardBackspace}
            disabled={isPlaying}
          />
        )}

        <section
          className="rounded-md p-4 sm:p-5 border flex flex-col gap-5"
          style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
          data-testid="harp-controls"
        >
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-5">
            <div className="flex-1 min-w-0">
              <label className="block text-[10px] uppercase tracking-[0.18em] mb-1.5" style={labelStyle}>
                Scale
              </label>
              <Select
                value={scale}
                onValueChange={(v) => {
                  setScaleValue(v);
                  clearHarpState();
                }}
              >
                <SelectTrigger
                  data-testid="harp-scale-select"
                  className="h-11 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] text-base"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <SelectValue placeholder="Pick a scale" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFBF2] border-[#EBE3D5] text-[#1A1523]">
                  {SCALES.map((s) => (
                    <SelectItem
                      key={s.value}
                      value={s.value}
                      data-testid={`harp-scale-option-${s.value}`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-[10px] uppercase tracking-[0.18em] mb-1.5" style={labelStyle}>
                Key
              </label>
              <Select
                value={root}
                onValueChange={(v) => {
                  setRootValue(v);
                  clearHarpState();
                }}
              >
                <SelectTrigger
                  data-testid="harp-root-select"
                  className="h-11 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] text-base"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <SelectValue placeholder="Pick a key" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFBF2] border-[#EBE3D5] text-[#1A1523]">
                  {ROOTS.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`harp-root-option-${r.value}`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-[1.4] min-w-0">
              <label className="block text-[10px] uppercase tracking-[0.18em] mb-1.5" style={labelStyle}>
                Mode (starting note)
              </label>
              <Select
                value={String(modeIdx)}
                onValueChange={(v) => {
                  setModeIdxValue(parseInt(v, 10));
                  clearHarpState();
                }}
              >
                <SelectTrigger
                  data-testid="harp-mode-select"
                  className="h-11 bg-[#FFFFFF] border-[#EBE3D5] text-[#1A1523] text-sm"
                  style={{ fontFamily: "'Courier Prime', monospace" }}
                >
                  <SelectValue placeholder="Pick a mode" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFBF2] border-[#EBE3D5] text-[#1A1523]">
                  {modeOptions.map((m) => (
                    <SelectItem
                      key={m.value}
                      value={String(m.value)}
                      data-testid={`harp-mode-option-${m.value}`}
                      style={{ fontFamily: "'Courier Prime', monospace" }}
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-5 pt-4"
            style={{ borderTop: "1px solid #EBE3D5" }}
          >
            <div className="min-w-0">
              <label className="block text-[10px] uppercase tracking-[0.18em] mb-1.5" style={labelStyle}>
                Feel
              </label>
              <div className="flex" data-testid="harp-playback-mode">
                {PLAYBACK_MODES.map((m, i) => {
                  const active = playbackMode === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setPlaybackMode(m.value)}
                      data-testid={`harp-playback-${m.value}`}
                      className="h-11 px-4 text-xs uppercase tracking-[0.16em] transition-colors"
                      style={{
                        fontFamily: "'Courier Prime', monospace",
                        border: "1px solid #EBE3D5",
                        borderLeftWidth: i === 0 ? 1 : 0,
                        backgroundColor: active ? "#1B6C7A" : "#FFFFFF",
                        color: active ? "#1A1523" : "#5C5268",
                      }}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 min-w-[170px]">
              <label
                className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.18em] mb-1.5"
                style={labelStyle}
                htmlFor="harp-bpm"
              >
                <span>Tempo</span>
                <span style={{ color: "#8A6514" }} data-testid="harp-bpm-value">
                  {bpm} bpm
                </span>
              </label>
              <div className="h-11 flex items-center gap-3">
                <input
                  id="harp-bpm"
                  type="range"
                  min={BPM_MIN}
                  max={BPM_MAX}
                  step={1}
                  value={bpm}
                  onChange={(e) =>
                    setBpmByMode((prev) => ({ ...prev, [playbackMode]: parseInt(e.target.value, 10) }))
                  }
                  data-testid="harp-bpm-slider"
                  className="studio-range flex-1 min-w-0"
                />
                <button
                  type="button"
                  onClick={() => {
                    setBpmByMode((p) => ({ ...p, [playbackMode]: harpBpmFor(playbackMode) }));
                    setSustainByMode((p) => ({ ...p, [playbackMode]: harpSustainFor(playbackMode) }));
                  }}
                  disabled={atFeelDefaults}
                  data-testid="harp-feel-reset"
                  className="text-[10px] uppercase tracking-[0.16em] transition-colors disabled:opacity-30 hover:text-[#8A6514]"
                  style={{ color: "#E86F4A", fontFamily: "'Courier Prime', monospace" }}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="flex-1 min-w-[150px]">
              <label
                className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.18em] mb-1.5"
                style={labelStyle}
                htmlFor="harp-sustain"
              >
                <span>Sustain</span>
                <span style={{ color: "#8A6514" }} data-testid="harp-sustain-value">
                  {sustain}%
                </span>
              </label>
              <div className="h-11 flex items-center">
                <input
                  id="harp-sustain"
                  type="range"
                  min={0}
                  max={200}
                  step={5}
                  value={sustain}
                  onChange={(e) =>
                    setSustainByMode((prev) => ({
                      ...prev,
                      [playbackMode]: parseInt(e.target.value, 10),
                    }))
                  }
                  data-testid="harp-sustain-slider"
                  className="studio-range w-full"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-4 sm:space-y-5">
          <LetterStrip theme="light"
            sequence={stripSequence}
            playedKeys={stripPlayed}
            activeKey={stripActive}
            onWordClick={isPlaying ? null : playWord}
            hint="Pluck a string, or type a word above."
          />
          {!usingTyped && pluckWords.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-3">
              <p
                className="text-xs"
                style={{ color: "#8C8198", fontFamily: "'Courier Prime', monospace" }}
                data-testid="harp-pluck-hint"
              >
                Your pauses split the words — {pluckWords.length}{" "}
                {pluckWords.length === 1 ? "word" : "words"} so far. Tap one to hear it again.
              </p>
              <div className="flex" data-testid="harp-rhythm-toggle">
                {[
                  { value: true, label: "My timing" },
                  { value: false, label: "In tempo" },
                ].map((opt, i) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setExactTiming(opt.value)}
                    disabled={isPlaying}
                    data-testid={`harp-rhythm-${opt.value ? "exact" : "tempo"}`}
                    className="h-8 px-3 text-[10px] uppercase tracking-[0.16em] transition-colors disabled:opacity-40"
                    style={{
                      fontFamily: "'Courier Prime', monospace",
                      border: "1px solid #EBE3D5",
                      borderLeftWidth: i === 0 ? 1 : 0,
                      backgroundColor: exactTiming === opt.value ? "#1B6C7A" : "#FFFFFF",
                      color: exactTiming === opt.value ? "#1A1523" : "#5C5268",
                    }}
                    title={
                      opt.value
                        ? "Play it back with the rhythm you plucked"
                        : "Play it back on the tempo grid"
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Harp theme="light"
            ref={harpRef}
            scaleOptions={scaleOptions}
            onPluck={strikeString}
            lit={lit}
          />
        </section>

        <section className="flex flex-wrap items-center justify-center gap-3">
          <PlayButton
            testId="harp-play-button"
            onClick={handlePlay}
            disabled={isPlaying || activeSequence.length === 0 || tooManyLetters}
            label={isPlaying ? "Playing…" : "Play"}
          />
          {!usingTyped && pluckLog.length > 0 && (
            <Button
              data-testid="harp-undo-pluck-button"
              onClick={undoPluck}
              disabled={isPlaying}
              variant="outline"
              className="rounded-full border-[#E86F4A] text-[#E86F4A] hover:bg-[#E86F4A]/10 hover:text-[#E86F4A] active:text-[#E86F4A] focus-visible:text-[#E86F4A]"
              style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
              title="Drop the last string you plucked (or press Backspace)"
            >
              ↶ Undo pluck
            </Button>
          )}

          <Button
            data-testid="harp-reset-button"
            onClick={reset}
            variant="outline"
            className="rounded-full border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 hover:text-[#1A1523] active:text-[#1A1523] focus-visible:text-[#1A1523]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
          >
            Reset
          </Button>
          <Button
            data-testid="harp-save-button"
            onClick={handleSave}
            disabled={saving || activeSequence.length === 0 || tooManyLetters}
            variant="outline"
            className="rounded-full border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 hover:text-[#1A1523] active:text-[#1A1523] focus-visible:text-[#1A1523]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            data-testid="harp-copy-link-button"
            onClick={copyShareLink}
            disabled={saving || activeSequence.length === 0 || tooManyLetters}
            variant="outline"
            className="rounded-full border-[#D99B26] text-[#8A6514] hover:bg-[#D99B26]/10 hover:text-[#8A6514] active:text-[#8A6514] focus-visible:text-[#8A6514]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
          >
            {savedId ? "Copy link" : "Share"}
          </Button>
        </section>
      </main>
    </div>
  );
}
